import { ConnectProto } from "@racoon/api";
import { useCallback, useState } from "react";
import {
  useArchiveContractMutation,
  useCreateContractMutation,
  useListContractsQuery,
  useUpdateContractMutation,
} from "~/shared/api/contract/contract.grpc";
import { fromTimestamp, toTimestamp } from "@racoon/lib";
import { toAmount } from "~/shared/lib/tariff-rate";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { skipToken } from "@reduxjs/toolkit/query";

type ContractUpdateField = Omit<ConnectProto.ContractUpdate, "id">;
export const useContractOperations = (receiverId?: number) => {
  const { activeCompanyId } = useAuthSession();

  const {
    data: contractsResponse,
    isLoading,
    refetch,
  } = useListContractsQuery(
    receiverId && activeCompanyId
      ? {
          targetOrganizationIds: [BigInt(receiverId)],
          serviceProviderIds: [BigInt(activeCompanyId)],
        }
      : skipToken,
  );

  const [updateContractMutation] = useUpdateContractMutation();
  const [archiveContractMutation] = useArchiveContractMutation();
  const [createContractMutation] = useCreateContractMutation();

  const [contractChanges, setContractChanges] = useState<
    Record<number, Partial<ContractUpdateField>>
  >({});

  const updateField = useCallback((contractId: number, field: string, value: any) => {
    setContractChanges((prev) => ({
      ...prev,
      [contractId]: { ...prev[contractId], [field]: value },
    }));
  }, []);

  const saveChanges = useCallback(
    async (contractId: number) => {
      const changes = contractChanges[contractId];
      if (!changes || Object.keys(changes).length === 0) return;

      // Convert UI changes to gRPC format (using camelCase field names)
      const updateData: Partial<ConnectProto.ContractUpdate> = {
        id: BigInt(contractId),
      };

      if (changes.dealNumber !== undefined) {
        updateData.dealNumber = changes.dealNumber;
      }
      if (changes.endAt !== undefined) {
        let date: Date;
        if (changes.endAt instanceof Date) {
          date = changes.endAt;
        } else if (typeof changes.endAt === "object" && "seconds" in changes.endAt) {
          // It's a Timestamp object
          date = fromTimestamp(changes.endAt) || new Date();
        } else {
          date = new Date(changes.endAt as any);
        }
        updateData.endAt = toTimestamp(date) as any;
      }
      if (changes.startAt !== undefined) {
        let date: Date;
        if (changes.startAt instanceof Date) {
          date = changes.startAt;
        } else if (typeof changes.startAt === "object" && "seconds" in changes.startAt) {
          // It's a Timestamp object
          date = fromTimestamp(changes.startAt) || new Date();
        } else {
          date = new Date(changes.startAt as any);
        }
        updateData.startAt = toTimestamp(date) as any;
      }
      if (changes.tariff !== undefined) {
        updateData.tariff = toAmount(Number(changes.tariff));
      }
      if (changes.rentCost !== undefined) {
        updateData.rentCost = toAmount(Number(changes.rentCost));
      }
      if (changes.status !== undefined) {
        updateData.status = changes.status;
      }

      await updateContractMutation(updateData as ConnectProto.ContractUpdate).unwrap();

      setContractChanges((prev) => {
        const newChanges = { ...prev };
        delete newChanges[contractId];
        return newChanges;
      });

      refetch();
    },
    [contractChanges, updateContractMutation, refetch],
  );

  const toggleArchive = useCallback(
    async (
      contractId: number,
      isInactive: boolean,
      serviceProviderId: bigint,
      targetOrganizationId: bigint,
    ) => {
      if (isInactive) {
        // Restore - set status to ACTIVE
        await updateContractMutation({
          id: BigInt(contractId),
          status: ConnectProto.ContractStatus.ACTIVE,
          serviceProviderId: serviceProviderId,
          targetOrganizationId: targetOrganizationId,
        }).unwrap();
      } else {
        // Archive
        await archiveContractMutation({
          id: BigInt(contractId),
          serviceProviderId: serviceProviderId,
          targetOrganizationId: targetOrganizationId,
        }).unwrap();
      }
      refetch();
    },
    [archiveContractMutation, updateContractMutation, refetch],
  );

  const refreshContracts = useCallback(() => {
    refetch();
  }, [refetch]);

  // Return contracts directly from gRPC (ConnectProto.Contract)
  const contracts: ConnectProto.Contract[] | undefined = contractsResponse?.contracts;

  return {
    contracts,
    contractsLoading: isLoading,
    contractChanges,
    updateField,
    saveChanges,
    toggleArchive,
    createContract: createContractMutation,
    refreshContracts,
  };
};
